import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { LOGO_URL, NAV_LINKS, FIRM } from "../../lib/firm";
import { List, X } from "@phosphor-icons/react";
import { trackEvent } from "../../lib/analytics";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // setScrolled is a stable useState setter and onScroll is local to this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[rgba(250,250,249,0.92)] border-b border-sage-soft"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
            aria-label="Retirement Tax Consultants home"
          >
            <img
              src={LOGO_URL}
              alt="Retirement Tax Consultants"
              className="h-10 md:h-12 lg:h-14 w-auto object-contain mix-blend-multiply"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `editorial-link text-sm tracking-wide transition-colors ${
                    isActive
                      ? "text-accent-gold"
                      : "text-deep-teal hover:text-deep-teal/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={FIRM.calendly15}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("nav_book_cta_click")}
              data-testid="nav-book-cta"
              className="hidden sm:inline-flex items-center px-5 py-2.5 text-xs tracking-overline uppercase font-medium bg-accent-gold !text-white hover:bg-accent-gold-hover transition-colors"
            >
              Schedule a Consultation
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle navigation"
              className="lg:hidden p-2 text-deep-teal"
            >
              {open ? <X size={24} weight="light" /> : <List size={24} weight="light" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-sage-soft bg-cream"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-base text-deep-teal"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <a
              href={FIRM.calendly15}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                setOpen(false);
                trackEvent("nav_book_cta_click");
              }}
              className="inline-flex items-center gap-2 text-base text-accent-gold font-semibold"
              data-testid="mobile-nav-book"
            >
              Schedule a Consultation →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
